// src/pages/admin/components/SpecializedAmenitiesPanel.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn } from '../ui';

export default function SpecializedAmenitiesPanel({ propertyId, propertyType }) {
  const [propertyAmenities, setPropertyAmenities] = React.useState([]);
  const [allAmenities, setAllAmenities] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [showPicker, setShowPicker] = React.useState(false);

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignPropertyAmenities,
          attach: AdminAPI.attachForeignPropertyAmenity,
          update: AdminAPI.updateForeignPropertyAmenity,
          delete: AdminAPI.deleteForeignPropertyAmenity,
          reorder: AdminAPI.reorderForeignPropertyAmenities,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanPropertyAmenities,
          attach: AdminAPI.attachSuburbanPropertyAmenity,
          update: AdminAPI.updateSuburbanPropertyAmenity,
          delete: AdminAPI.deleteSuburbanPropertyAmenity,
          reorder: AdminAPI.reorderSuburbanPropertyAmenities,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentPropertyAmenities,
          attach: AdminAPI.attachCommercialRentPropertyAmenity,
          update: AdminAPI.updateCommercialRentPropertyAmenity,
          delete: AdminAPI.deleteCommercialRentPropertyAmenity,
          reorder: AdminAPI.reorderCommercialRentPropertyAmenities,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingPropertyAmenities,
          attach: AdminAPI.attachParkingPropertyAmenity,
          update: AdminAPI.updateParkingPropertyAmenity,
          delete: AdminAPI.deleteParkingPropertyAmenity,
          reorder: AdminAPI.reorderParkingPropertyAmenities,
        };
      default:
        return {
          list: AdminAPI.listPropertyAmenities,
          attach: AdminAPI.attachPropertyAmenity,
          update: AdminAPI.updatePropertyAmenity,
          delete: AdminAPI.deletePropertyAmenity,
          reorder: AdminAPI.reorderPropertyAmenities,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const load = React.useCallback(async () => {
    if (busy) return; // Предотвращаем повторные запросы
    
    setBusy(true);
    setMsg('');
    try {
      const [propResp, allResp] = await Promise.all([
        api.list(propertyId),
        AdminAPI.listAmenities()
      ]);
      
      setPropertyAmenities(Array.isArray(propResp?.amenities) ? propResp.amenities : []);
      setAllAmenities(Array.isArray(allResp?.amenities) ? allResp.amenities : []);
    } catch (e) {
      console.error('Error loading amenities:', e);
      setMsg(e.message || 'Не удалось загрузить удобства');
    } finally {
      setBusy(false);
    }
  }, [propertyId, propertyType, busy]);

  React.useEffect(() => { 
    if (propertyId) {
      load(); 
    }
  }, [propertyId, propertyType]);

  const attachedAmenityIds = propertyAmenities.map(pa => pa.amenity_id || pa.amenity?.id);
  const availableAmenities = allAmenities.filter(a => !attachedAmenityIds.includes(a.id));

  const handleAttach = async (amenityId) => {
    setBusy(true);
    try {
      await api.attach(propertyId, {
        amenity_id: amenityId,
        sort_order: propertyAmenities.length,
        is_active: true,
      });
      await load();
      setMsg('Удобство добавлено');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка добавления удобства');
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async (linkId) => {
    if (!window.confirm('Убрать удобство из объекта?')) return;
    setBusy(true);
    try {
      await api.delete(propertyId, linkId);
      await load();
      setMsg('Удобство убрано');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка удаления удобства');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (linkId, isActive) => {
    setBusy(true);
    try {
      await api.update(propertyId, linkId, { is_active: !isActive });
      await load();
    } catch (e) {
      setMsg(e.message || 'Ошибка изменения статуса');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Удобства ({propertyAmenities.length})</h4>
        <Actions style={{ margin: 0 }}>
          <Btn onClick={() => setShowPicker(!showPicker)} disabled={busy}>
            {showPicker ? 'Скрыть каталог' : '+ Добавить удобства'}
          </Btn>
        </Actions>
      </div>

      {msg && <div style={{ color: msg.includes('Ошибка') ? '#e66' : '#1b5e20', marginBottom: 8 }}>{msg}</div>}

      {showPicker && (
        <Card style={{ marginBottom: 16, background: '#f8f9fa' }}>
          <h5>Каталог удобств</h5>
          {availableAmenities.length === 0 ? (
            <div style={{ color: '#777', padding: 10 }}>
              Все доступные удобства уже добавлены к объекту.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 8 }}>
              {availableAmenities.map((amenity) => (
                <div
                  key={amenity.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 8,
                    border: '1px solid var(--bdr)',
                    borderRadius: 4,
                    background: '#fff',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {amenity.name_ru || amenity.name || 'Без названия'}
                    </div>
                    {amenity.name_en && (
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {amenity.name_en}
                      </div>
                    )}
                  </div>
                  <Btn
                    onClick={() => handleAttach(amenity.id)}
                    disabled={busy}
                    style={{ fontSize: 12, padding: '4px 8px' }}
                  >
                    Добавить
                  </Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {busy && <div style={{ opacity: 0.6 }}>Загрузка...</div>}

      <div style={{ display: 'grid', gap: 8 }}>
        {propertyAmenities.map((propAmenity) => {
          const amenity = propAmenity.amenity || propAmenity;
          return (
            <div
              key={propAmenity.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 12,
                border: '1px solid var(--bdr)',
                borderRadius: 4,
                background: propAmenity.is_active ? '#fff' : '#f5f5f5',
                opacity: propAmenity.is_active ? 1 : 0.6,
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>
                  {amenity.name_ru || amenity.name || 'Без названия'}
                  {!propAmenity.is_active && <span style={{ color: '#e66', marginLeft: 8 }}>• Неактивно</span>}
                </div>
                {amenity.name_en && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {amenity.name_en}
                  </div>
                )}
                {amenity.description_ru && (
                  <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                    {amenity.description_ru}
                  </div>
                )}
              </div>
              
              <Actions style={{ margin: 0 }}>
                <Btn
                  onClick={() => toggleActive(propAmenity.id, propAmenity.is_active)}
                  disabled={busy}
                  style={{ fontSize: 12, padding: '4px 8px' }}
                >
                  {propAmenity.is_active ? 'Скрыть' : 'Показать'}
                </Btn>
                <Btn
                  onClick={() => handleDetach(propAmenity.id)}
                  disabled={busy}
                  style={{ fontSize: 12, padding: '4px 8px', color: '#e66' }}
                >
                  Убрать
                </Btn>
              </Actions>
            </div>
          );
        })}
      </div>

      {propertyAmenities.length === 0 && !busy && (
        <div style={{ textAlign: 'center', padding: 20, color: '#777' }}>
          Удобства не добавлены. Нажмите "Добавить удобства" для выбора из каталога.
        </div>
      )}
    </Card>
  );
}